import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchCart, clearCart } = useCart();
  const { user } = useAuth();
  const sessionId = searchParams.get('session_id');
  const isGuest = searchParams.get('guest') === 'true';

  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, {
          withCredentials: true
        });

        setPaymentInfo(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Clear cart after successful payment
          if (user) {
            fetchCart();
          } else {
            // Clear guest cart from localStorage
            localStorage.removeItem('guest_cart');
            clearCart();
          }
        } else if (data.status === 'expired') {
          setStatus('error');
        } else if (attempts < 10) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Failed to check payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, navigate, attempts, fetchCart, clearCart, user]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="checkout-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Verifying payment...</p>
          <p className="text-[#A1A7B5] mt-2">Please wait while we confirm your order</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="checkout-error">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-[#FF3B30]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-[#FF3B30] text-4xl">!</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Payment Failed
          </h1>
          <p className="text-[#A1A7B5] mb-8">
            We couldn't process your payment. Please try again or contact support.
          </p>
          <div className="space-y-4">
            <Link to="/checkout">
              <Button className="btn-primary w-full">
                Try Again
              </Button>
            </Link>
            <Link to="/products">
              <Button className="btn-secondary w-full">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center py-12" data-testid="checkout-success">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-20 h-20 bg-[#00F0FF]/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
          <CheckCircle className="w-12 h-12 text-[#00F0FF]" />
        </div>

        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Order Confirmed!
        </h1>

        <p className="text-[#A1A7B5] mb-8">
          Thank you for your purchase. Your order has been successfully placed and is being processed.
          {isGuest && " A confirmation email will be sent to your email address."}
        </p>

        {paymentInfo && (
          <div className="glass-card p-6 mb-8 text-left">
            <div className="flex items-center space-x-3 mb-4">
              <Package className="w-6 h-6 text-[#00F0FF]" />
              <h2 className="text-lg font-semibold text-white">Order Details</h2>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#A1A7B5]">Order ID</span>
                <span className="text-white font-mono">{paymentInfo.order_id?.slice(0, 8)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A1A7B5]">Amount Paid</span>
                <span className="text-[#00F0FF] font-semibold">
                  ${(paymentInfo.amount_total / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A1A7B5]">Status</span>
                <span className="text-green-400 font-medium">Paid</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {user && (
            <Link to="/orders">
              <Button className="btn-primary w-full flex items-center justify-center space-x-2" data-testid="view-orders-btn">
                <span>View Your Orders</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          )}
          <Link to="/products">
            <Button className={`${user ? 'btn-secondary' : 'btn-primary'} w-full`} data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </Link>
          {!user && (
            <p className="text-[#A1A7B5] text-sm">
              <Link to="/register" className="text-[#00F0FF] hover:underline">Create an account</Link> to track your orders and enjoy faster checkout.
            </p>
          )}
        </div>

        <p className="text-[#A1A7B5] text-sm mt-8">
          A confirmation email will be sent to your registered email address.
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
